import { Classification, ClassificationGroups } from "@/api/studio/data/types/classification.type";

/*
 * Maps taxonomy class names to colors for display in the
 * UI.
 */
export const classColors: Record<string, string> = {
  logo: "#F2318A",
  icon: "#AF2780",
  mascot: "#FF0162",
  "product-image": "#8636ED",
  "product-in-use": "#5C13B9",
  "product-group": "#290092",
  "lifestyle-image": "#C17700",
  variant: "#0F6CEA",
  "brand-narrative": "#006F77",
  claim: "#019AA8",
  promotion: "#00A851",
  "call-to-action": "#00C148",
  price: "#9300E9",
  "trust-mark": "#007635",
  "regulatory-information": "#608483",
  size: "#0F79EA",
  area: "#F2318A", // Default color for scene AOIs (will be overridden by color)
  other: "#1B1B1D",
};

/*
 * Maps taxonomy class names to display names for display in
 * the UI.
 */
export const classNames: Record<string, string> = {
  logo: "Logo",
  icon: "Icon",
  mascot: "Mascot",
  "product-image": "Product image",
  "product-in-use": "Product in use",
  "product-group": "Product group",
  "lifestyle-image": "Lifestyle image",
  "brand-narrative": "Brand narrative",
  claim: "Claim",
  promotion: "Promotion",
  "call-to-action": "Call to action",
  price: "Price",
  "trust-mark": "Trust mark",
  "regulatory-information": "Regulatory information",
  size: "Size",
  variant: "Variant",
  area: "Area",
  other: "Other",
};

/*
 * Maps display names to their respective key colors.
 */
export const displayNameToColor: Record<string, string> = {
  Logo: "#F2318A",
  Icon: "#AF2780",
  Mascot: "#FF0162",
  "Product image": "#8636ED",
  "Product in use": "#5C13B9",
  "Product group": "#290092",
  "Lifestyle image": "#C17700",
  Variant: "#0F6CEA",
  "Brand narrative": "#006F77",
  Claim: "#019AA8",
  Promotion: "#00A851",
  "Call to action": "#00C148",
  Price: "#9300E9",
  "Trust mark": "#007635",
  "Regulatory information": "#608483",
  Size: "#0F79EA",
  Area: "#F2318A",
  Other: "#1B1B1D",
};

export type GroupedClassification = {
  [key in Classification]?: {
    displayName: string;
    color: string;
    description: string;
  };
};

export type GroupedClassifications = {
  [key in ClassificationGroups]: GroupedClassification;
};

/*
 * Maps taxonomy class names to grouped classifications for display in
 * the UI.
 */
export const groupedClassifications: GroupedClassifications = {
  Branding: {
    logo: {
      displayName: "Logo",
      color: "#F2318A",
      description: "The brand’s primary visual symbol.",
    },
    icon: {
      displayName: "Icon",
      color: "#AF2780",
      description: "Simplified graphic tied to the brand or product.",
    },
    mascot: {
      displayName: "Mascot",
      color: "#FF0162",
      description: "Character representing the brand.",
    },
  },
  Product: {
    "product-image": {
      displayName: "Product image",
      color: "#8636ED",
      description: "Visual of the product, including packaging.",
    },
    "product-in-use": {
      displayName: "Product in use",
      color: "#5C13B9",
      description: "Product shown in a realistic setting.",
    },
    "product-group": {
      displayName: "Product group",
      color: "#290092",
      description: "Multiple products displayed together.",
    },
    price: {
      displayName: "Price",
      color: "#9300E9",
      description: "Visuals or text showing product cost.",
    },
    variant: {
      displayName: "Variant",
      color: "#0F6CEA",
      description: "Different versions of the same product.",
    },
    size: {
      displayName: "Size",
      color: "#0F79EA",
      description: "Text or visuals showing product size.",
    },
  },
  Messaging: {
    "brand-narrative": {
      displayName: "Brand narrative",
      color: "#006F77",
      description: "Communicates the brand's story or values.",
    },
    claim: {
      displayName: "Claim",
      color: "#019AA8",
      description: "Statement of product features or benefits.",
    },
    "call-to-action": {
      displayName: "Call to action",
      color: "#00C148",
      description: "Elements prompting specific actions.",
    },
    promotion: {
      displayName: "Promotion",
      color: "#00A851",
      description: "Text highlighting an offer (e.g. 50% Off).",
    },
    "trust-mark": {
      displayName: "Trust mark",
      color: "#007635",
      description: "Visual for certifications or awards (e.g., Fair Trade).",
    },
    "regulatory-information": {
      displayName: "Regulatory information",
      color: "#608483",
      description: "Required information (e.g., nutrition, recycling).",
    },
  },
  Scene: {
    "lifestyle-image": {
      displayName: "Lifestyle image",
      color: "#C17700",
      description: "Imagery evoking the product's intended environment.",
    },
  },
  Other: {
    other: {
      displayName: "Other",
      color: "#1B1B1D",
      description: "Other elements not covered by the above classifications.",
    },
  },
};
